import { X } from "lucide-react";
import { useEffect } from "react";
import type { ReactNode } from "react";
import clsx from "clsx";

interface DrawerProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    children: ReactNode;
    footer?: ReactNode;
}

export function Drawer({ open, onClose, title, description, children, footer }: DrawerProps) {
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <button
                type="button"
                aria-label="Close panel"
                onClick={onClose}
                className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            />
            <aside
                className={clsx(
                    "relative flex h-full w-full max-w-md flex-col border-l app-shell-border",
                    "app-shell-panel shadow-xl",
                )}
            >
                <header className="flex items-start justify-between gap-4 border-b app-shell-border p-5">
                    <div className="min-w-0">
                        <h2 className="app-text-primary text-lg font-semibold">{title}</h2>
                        {description && (
                            <p className="app-shell-text-muted mt-0.5 text-sm">{description}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="app-shell-text-muted rounded-lg p-1.5 transition-colors hover:app-content-subtle-hover"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="app-scroll flex-1 overflow-y-auto p-5">{children}</div>

                {footer && (
                    <footer className="border-t app-shell-border p-5">{footer}</footer>
                )}
            </aside>
        </div>
    );
}
